import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import type { Me } from '../auth/me';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { formatDecimal, parseScaled } from '../ui/numbers';
import { ExpenseForm } from './ExpenseForm';
import { SettlementContract } from './SettlementContract';
import { dayLabel, hoursLabel, KINDS, METHODS, multiplierLabel, settlementKey, SETTLEMENTS_KEY, SETTLEMENTS_RESOURCE, STATES, type Item, type Settlement } from './types';
import { NotesInput } from '../ui/NotesInput';
import { sameValues, useOpening, useRowGuard, useUnsavedGuard } from '../ui/unsaved';

const money = (v: number | undefined) => (v === undefined ? '—' : formatPln(v));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);
/** The user's scope of an action on settlements ('own': their own person only), or null without the right. */
export const scopeOf = (me: Me, action: string): 'all' | 'own' | null => {
  if (me.isOwner) return 'all';
  const found = me.permissions.filter((p) => p.resource === SETTLEMENTS_RESOURCE && p.action === action);
  return found.some((p) => p.scope === 'all') ? 'all' : found.length > 0 ? 'own' : null;
};

/** What an item counts: "2 dni × 500,00 zł", "3,5 h × 1,5". */
function detail(i: Item): string {
  const rate = i.rateGrosze === undefined ? '' : ` × ${formatPln(i.rateGrosze)}`;
  if (i.kind === 'dniowka') return `${formatDecimal(i.daysMilli / 1000)} ${i.daysMilli === 1000 ? 'dzień' : 'dni'}${rate}`;
  if (i.kind === 'nadgodziny') return `${hoursLabel(i.hoursMilli)} h × ${multiplierLabel(i.multiplierBp)}${i.rateGrosze === undefined ? '' : ` (dniówka ${formatPln(i.rateGrosze)})`}`;
  return '';
}

/**
 * One item: a day rate or overtime may get another rate (and days), an expense / manual item / deduction another
 * amount — saved on Enter or leaving the field; × deletes a hand-made item. Read-only once approved.
 */
function ItemRow({ item, editable, onChanged }: { item: Item; editable: boolean; onChanged: () => void }) {
  const counted = item.kind === 'dniowka' || item.kind === 'nadgodziny';
  const value = counted ? item.rateGrosze : item.amountGrosze;
  const [money_, setMoney] = useState(value === undefined ? '' : plnToInput(value));
  const [days, setDays] = useState(formatDecimal(item.daysMilli / 1000));
  const [error, setError] = useState('');
  useEffect(() => {
    setMoney(value === undefined ? '' : plnToInput(value));
    setDays(formatDecimal(item.daysMilli / 1000));
  }, [item.version, value, item.daysMilli]);
  const canEditMoney = editable && value !== undefined;
  /** Saves what changed; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    const body: Record<string, number> = {};
    try {
      if (canEditMoney) {
        const grosze = parsePln(money_);
        if (grosze !== value) body[counted ? 'rateGrosze' : 'amountGrosze'] = grosze;
      }
      if (editable && item.kind === 'dniowka') {
        const daysMilli = parseScaled(days, 3);
        if (daysMilli === null || daysMilli < 0) throw new Error('Dni: liczba, np. 1,5');
        if (daysMilli !== item.daysMilli) body.daysMilli = daysMilli;
      }
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
    if (Object.keys(body).length === 0) return true;
    let ok = true;
    try {
      await api(`/api/settlements/items/${item.id}`, { method: 'PATCH', body: { ...body, version: item.version } });
      setError('');
    } catch (e) {
      ok = false;
      setError(e instanceof ApiError && e.status === 409 ? 'Ktoś w tym czasie zmienił tę pozycję. Pokazuję aktualne wartości.' : errorText(e, 'Nie udało się zapisać'));
    }
    onChanged();
    return ok;
  }
  // owner 2026-10-23 21:14: typed and not saved (leaving the cell saves): leaving the card or tab waits or asks
  const row = useRowGuard(editable && !sameValues({ money_, days }, { money_: value === undefined ? '' : plnToInput(value), days: formatDecimal(item.daysMilli / 1000) }), persist);
  const save = row.save;
  async function remove() {
    try {
      await api(`/api/settlements/items/${item.id}?version=${item.version}`, { method: 'DELETE' });
    } catch (e) {
      setError(errorText(e, 'Nie udało się usunąć'));
    }
    onChanged();
  }
  const keys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
  };
  const label = `${KINDS[item.kind]}${item.description ? `: ${item.description}` : ''}`;
  return (
    <>
      <tr ref={row.ref}>
        <td>{KINDS[item.kind]}</td>
        <td>
          {item.description || (item.kind === 'dniowka' ? 'z obsady' : '')}
          {item.receiptFileId && (
            <>
              {' · '}
              <a href={`/api/files/${item.receiptFileId}`} target="_blank" rel="noreferrer">
                Paragon
              </a>
            </>
          )}
          {item.kind === 'dniowka' && !item.auto && <span className="muted"> · stawka wpisana ręcznie</span>}
        </td>
        <td className="stl-col-detail">
          {editable && item.kind === 'dniowka' ? (
            <span className="stl-days">
              <NumberInput className="cell-input num" aria-label={`Dni: ${label}`} value={days} onValueChange={setDays} onBlur={() => void save()} onKeyDown={keys} /> dni
            </span>
          ) : (
            detail(item)
          )}
        </td>
        <td className="num stl-col-rate">
          {canEditMoney ? (
            <NumberInput className="cell-input num" kind="money" aria-label={`${counted ? 'Stawka' : 'Kwota'}: ${label}`} value={money_} onValueChange={setMoney} onBlur={() => void save()} onKeyDown={keys} />
          ) : counted ? (
            money(item.rateGrosze)
          ) : null}
        </td>
        <td className="num">{item.amountGrosze === undefined ? '—' : `${item.kind === 'potracenie' ? '−' : ''}${formatPln(item.amountGrosze)}`}</td>
        <td className="q-actions">
          {editable && !(item.auto && item.kind === 'dniowka') && item.kind !== 'nadgodziny' && (
            <button className="btn icon ghost" type="button" aria-label={`Usuń pozycję: ${label}`} title="Usuń pozycję" onClick={() => void remove()}>
              ×
            </button>
          )}
        </td>
      </tr>
      {error && (
        <tr className="sheet-row-error">
          <td colSpan={6} role="alert">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function PayoutForm({ s, onPaid }: { s: Settlement; onPaid: () => void }) {
  const due = s.totalGrosze !== undefined && s.paidGrosze !== undefined ? Math.max(0, s.totalGrosze - s.paidGrosze) : null;
  const [method, setMethod] = useState<keyof typeof METHODS>('przelew');
  const [amount, setAmount] = useState(due ? plnToInput(due) : '');
  const [paidOn, setPaidOn] = useState(() => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date()));
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');
  const pay = useMutation({
    mutationFn: (amountGrosze: number) => api(`/api/settlements/${s.id}/payouts`, { method: 'POST', body: { method, amountGrosze, paidOn, ...(note.trim() ? { note: note.trim() } : {}), version: s.version } }),
    onSuccess: () => {
      setNote('');
      onPaid();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) onPaid();
    },
  });
  function grosze(): number | null {
    try {
      const g = parsePln(amount);
      setLocal('');
      return g;
    } catch (err) {
      setLocal((err as Error).message);
      return null;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const g = grosze();
    if (g !== null) pay.mutate(g);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ method, amount, paidOn, note });
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ method, amount, paidOn, note }, opening),
    save: async () => {
      const g = grosze();
      if (g === null) return false;
      await pay.mutateAsync(g);
      return true;
    },
  });
  const error = local || (pay.error instanceof ApiError && pay.error.status === 409 ? 'Ktoś w tym czasie zmienił to rozliczenie (np. zapisał wypłatę). Pokazuję aktualne kwoty.' : pay.error ? errorText(pay.error, 'Nie udało się zapisać wypłaty') : '');
  return (
    <form ref={guardRef} className="fin-add" aria-label="Wypłata" data-feature-id="settlements.payouts" onSubmit={submit}>
      <label className="field">
        <span>Sposób</span>
        <select value={method} onChange={(e) => setMethod(e.target.value as keyof typeof METHODS)}>
          {(Object.keys(METHODS) as Array<keyof typeof METHODS>).map((m) => (
            <option key={m} value={m}>
              {METHODS[m]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Kwota (zł)</span>
        <NumberInput kind="money" value={amount} onValueChange={setAmount} />
      </label>
      <label className="field">
        <span>Data</span>
        <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </label>
      <label className="field">
        <span>Uwagi</span>
        <NotesInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button className="btn" type="submit" disabled={pay.isPending}>
        Dodaj wypłatę
      </button>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * The card of one person's settlement for one event (F39): items (day rate from the crew, overtime, expenses with a
 * receipt, manual items, deductions), "Zatwierdź" (freezes the items), payouts. Amounts the reader may not see are
 * not sent by the server; their own settlement always shows them.
 */
export function SettlementCard({ id, me }: { id: string; me: Me }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: settlementKey(id), queryFn: () => api<Settlement>(`/api/settlements/${id}`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY });
  const action = useMutation({
    mutationFn: (path: 'approve' | 'reopen') => api<Settlement>(`/api/settlements/${id}/${path}`, { method: 'POST', body: { version: query.data!.version } }),
    onSuccess: refresh,
  });
  const removePayout = useMutation({ mutationFn: (payoutId: string) => api(`/api/settlements/payouts/${payoutId}`, { method: 'DELETE' }), onSuccess: refresh });
  const s = query.data;
  if (query.isLoading) return <p className="muted" role="status">Wczytywanie…</p>;
  if (query.error || !s) return <p className="alert" role="alert">{errorText(query.error, 'Nie udało się wczytać rozliczenia')}</p>;
  const editScope = scopeOf(me, 'edit');
  const open = s.state === 'otwarte';
  const editable = open && editScope === 'all';
  const mayApprove = scopeOf(me, 'approve') === 'all';
  const mayPay = scopeOf(me, 'pay') === 'all';
  const due = s.totalGrosze !== undefined && s.paidGrosze !== undefined ? s.totalGrosze - s.paidGrosze : undefined;
  return (
    <section className="stl-card" aria-label={`Rozliczenie: ${s.personName}`} data-feature-id="settlements.card">
      <div className="stl-head">
        <div>
          <h2>{s.personName}</h2>
          <p className="muted">
            {s.eventName}
            {s.eventFirstDay && ` · ${dayLabel(s.eventFirstDay)}`}
          </p>
        </div>
        <span className={`stl-state stl-${s.state}`} data-testid="settlement-state">
          {STATES[s.state]}
        </span>
      </div>
      <SettlementContract settlement={s} me={me} mayEdit={editScope === 'all'} onChanged={refresh} />
      {action.error instanceof ApiError && action.error.status === 409 ? (
        <ConflictNotice
          onReload={() => {
            action.reset();
            refresh();
          }}
        />
      ) : (
        action.error && (
          <p className="alert" role="alert">
            {errorText(action.error, 'Nie udało się')}
          </p>
        )
      )}
      <div className="table-wrap">
        <table className="table fin-table" aria-label="Pozycje rozliczenia">
          <thead>
            <tr>
              <th scope="col">Rodzaj</th>
              <th scope="col">Opis</th>
              <th scope="col" className="stl-col-detail">
                Wyliczenie
              </th>
              <th scope="col" className="num stl-col-rate">
                Stawka
              </th>
              <th scope="col" className="num">
                Kwota
              </th>
              <th scope="col">
                <span className="visually-hidden">Działania</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {s.items.map((i) => (
              <ItemRow key={i.id} item={i} editable={editable} onChanged={refresh} />
            ))}
            {s.items.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Brak pozycji.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={2}>
                Razem do wypłaty
              </th>
              <td className="stl-col-detail" />
              <td className="stl-col-rate" />
              <td className="num" data-testid="settlement-total">
                {money(s.totalGrosze)}
              </td>
              <td />
            </tr>
            <tr>
              <th scope="row" colSpan={2}>
                Wypłacono
              </th>
              <td className="stl-col-detail" />
              <td className="stl-col-rate" />
              <td className="num">{money(s.paidGrosze)}</td>
              <td />
            </tr>
            <tr>
              <th scope="row" colSpan={2}>
                Pozostało
              </th>
              <td className="stl-col-detail" />
              <td className="stl-col-rate" />
              <td className="num" data-testid="settlement-due">
                {money(due)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {open && editScope !== null && <ExpenseForm settlementId={s.id} onlyExpenses={editScope === 'own'} onAdded={refresh} />}
      {!open && <p className="hint">Rozliczenie zatwierdzone: pozycji nie można już zmieniać{me.isOwner ? ' (możesz je ponownie otworzyć)' : ''}.</p>}
      <div className="actions">
        {open && mayApprove && (
          <button className="btn primary" type="button" disabled={action.isPending} onClick={() => action.mutate('approve')}>
            Zatwierdź
          </button>
        )}
        {!open && me.isOwner && (
          <button className="btn" type="button" disabled={action.isPending} onClick={() => action.mutate('reopen')}>
            Otwórz ponownie
          </button>
        )}
      </div>
      <h3>Wypłaty</h3>
      {s.payouts.length === 0 ? (
        <p className="muted">Brak wypłat.</p>
      ) : (
        <ul className="stl-payouts">
          {s.payouts.map((p) => (
            <li key={p.id}>
              {dayLabel(p.paidOn)} · {METHODS[p.method]} · {money(p.amountGrosze)}
              {p.note && <span className="muted"> · {p.note}</span>}
              {mayPay && (
                <button className="btn icon ghost" type="button" aria-label={`Usuń wypłatę z ${dayLabel(p.paidOn)}`} title="Usuń wypłatę" onClick={() => removePayout.mutate(p.id)}>
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!open && mayPay && s.state !== 'wyplacone' && <PayoutForm key={s.version} s={s} onPaid={refresh} />}
      <p>
        <Link to={`/wydarzenia?wydarzenie=${s.eventId}&zakladka=rozliczenia`}>Wydarzenie: {s.eventName}</Link>
      </p>
    </section>
  );
}
